import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Save, Send, Lock, CheckCircle2, History, GitCompare } from "lucide-react";
import { toast } from "sonner";

export default function AssessmentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [responses, setResponses] = useState({});
  const [autosavedAt, setAutosavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewVersion, setViewVersion] = useState("draft");
  const [historical, setHistorical] = useState(null);
  const dirty = useRef(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/assessments/${id}`);
    setPayload(data);
    setResponses(data.assessment.draft_responses || {});
    setAutosavedAt(data.assessment.autosaved_at);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isTac = user.role === "tac";
  const status = payload?.assessment?.status;
  const canEdit = isTac && (status === "draft" || status === "validated" || status === "follow_up_open" || status === "closed" || status === "in_validation");
  // Editing draft is always allowed for owner; resubmission creates a new version.
  const editable = isTac && viewVersion === "draft";

  const saveDraft = useCallback(async (data) => {
    setSaving(true);
    try {
      const r = await api.patch(`/assessments/${id}/draft`, { responses: data });
      setAutosavedAt(r.data.autosaved_at);
      dirty.current = false;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id]);

  // autosave (debounced)
  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(() => saveDraft(responses), 1500);
    return () => clearTimeout(t);
  }, [responses, saveDraft]);

  const setResp = (qid, field, value) => {
    dirty.current = true;
    setResponses((prev) => ({ ...prev, [qid]: { ...prev[qid], [field]: value } }));
  };

  const onVersionChange = async (v) => {
    setViewVersion(v);
    if (v === "draft") { setHistorical(null); return; }
    const sub = payload.submissions.find((s) => String(s.version) === v);
    if (sub) {
      const { data } = await api.get(`/assessments/submissions/${sub.id}`);
      setHistorical(data);
    }
  };

  const submit = async () => {
    await saveDraft(responses);
    try {
      const { data } = await api.post(`/assessments/${id}/submit`);
      toast.success(data.message);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Which responses to render: draft (editable) or historical snapshot (read-only)
  const histMap = useMemo(() => {
    if (!historical) return null;
    const m = {};
    (historical.responses || []).forEach((r) => { m[r.question_id] = r; });
    return m;
  }, [historical]);

  if (!payload) return <Loader label="Loading assessment" />;
  const { framework, assessment, submissions } = payload;

  const answeredCount = Object.values(responses).filter((r) => r && (r.score !== undefined && r.score !== null && r.score !== "")).length;
  const totalQ = framework.reduce((n, p) => n + p.questions.length, 0);

  return (
    <div>
      <PageHeader
        overline={`Assessment · ${assessment.tac_org || assessment.tac_name}`}
        title={assessment.title}
        description={isTac ? "Answer each question, save as draft (autosaved), then submit an immutable version." : "Read-only view of this partner's assessment."}
        actions={
          <div className="flex items-center gap-2">
            {submissions.length > 0 && (
              <Select value={viewVersion} onValueChange={onVersionChange}>
                <SelectTrigger className="w-[160px] rounded-md" data-testid="version-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{isTac ? "Working draft" : "Latest draft"}</SelectItem>
                  {submissions.map((s) => (
                    <SelectItem key={s.id} value={String(s.version)}>Version v{s.version}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <StatusBadge status={assessment.status} />
          </div>
        }
      />

      {viewVersion !== "draft" && (
        <div className="mb-5 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="historical-banner">
          <History className="h-4 w-4 shrink-0" aria-hidden="true" />
          Viewing historical version <span className="font-mono font-semibold">v{viewVersion}</span> — read-only immutable snapshot submitted {fmtDate(historical?.submitted_at)}.
        </div>
      )}

      {editable && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold text-foreground">{answeredCount}/{totalQ}</span> questions answered
            {autosavedAt && <span className="ml-3 text-xs">· Autosaved {fmtDate(autosavedAt)}</span>}
            {saving && <span className="ml-2 text-xs text-amber-600">saving…</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => saveDraft(responses)} className="rounded-md" data-testid="save-draft-button">
              <Save className="h-4 w-4 mr-1.5" aria-hidden="true" /> Save draft
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="rounded-md" data-testid="submit-assessment-button">
                  <Send className="h-4 w-4 mr-1.5" aria-hidden="true" /> Submit version
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-md">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading">Submit immutable version?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This creates a permanent, read-only snapshot (v{(assessment.current_version || 0) + 1}) and sends it for validation.
                    {assessment.current_version > 0 && " Prior versions remain unchanged."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={submit} className="rounded-md" data-testid="confirm-submit-button">Submit</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      <Tabs defaultValue={framework[0]?.id} className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-muted/60 p-1 rounded-md">
          {framework.map((p, i) => (
            <TabsTrigger key={p.id} value={p.id} className="rounded-sm data-[state=active]:bg-card" data-testid={`pillar-tab-${i}`}>
              <span className="font-mono text-xs mr-1.5 text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>{p.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {framework.map((p) => (
          <TabsContent key={p.id} value={p.id} className="mt-5">
            <div className="mb-4">
              <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.description} · Pillar weight {p.weight}</p>
            </div>
            <div className="space-y-3">
              {p.questions.map((q, qi) => {
                const hist = histMap ? histMap[q.id] : null;
                const current = hist || responses[q.id] || {};
                const readOnly = !editable;
                return (
                  <div key={q.id} className="bg-card border border-border rounded-md p-5" data-testid={`question-${q.id}`}>
                    <div className="flex items-start gap-3">
                      <span className="font-mono text-xs text-muted-foreground mt-1">{qi + 1}.</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{q.text}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{q.guidance}</p>

                        <div className="mt-4 flex items-center gap-2">
                          {Array.from({ length: (q.max_score || 5) + 1 }).map((_, sc) => {
                            const selected = String(current.score) === String(sc);
                            return (
                              <button
                                key={sc}
                                type="button"
                                disabled={readOnly}
                                onClick={() => setResp(q.id, "score", sc)}
                                data-testid={`score-${q.id}-${sc}`}
                                className={
                                  "h-9 w-9 rounded-md border text-sm font-semibold font-mono transition-colors " +
                                  (selected
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-card text-foreground border-border hover:border-slate-400") +
                                  (readOnly ? " opacity-70 cursor-not-allowed" : "")
                                }
                              >
                                {sc}
                              </button>
                            );
                          })}
                          <span className="text-xs text-muted-foreground ml-2">0 = none · {q.max_score} = fully embedded</span>
                        </div>

                        <Textarea
                          value={current.note || ""}
                          onChange={(e) => setResp(q.id, "note", e.target.value)}
                          disabled={readOnly}
                          placeholder="Add supporting notes or evidence references…"
                          className="mt-3 rounded-md text-sm"
                          data-testid={`note-${q.id}`}
                        />
                        {readOnly && <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3 w-3" aria-hidden="true" /> Read-only</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {submissions.length > 0 && (
        <div className="mt-8 bg-card border border-border rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Submitted versions</div>
            {submissions.length >= 2 && (
              <Button size="sm" variant="outline" className="rounded-md" onClick={() => navigate(`/assessments/${id}/compare`)} data-testid="compare-versions-button">
                <GitCompare className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Compare versions
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {submissions.map((s) => (
              <button key={s.id} onClick={() => navigate(`/reports/${s.id}`)}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-mono hover:border-slate-400 transition-colors"
                data-testid={`submission-chip-${s.version}`}>
                v{s.version} · {fmtDate(s.submitted_at)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
